/**
 * Schema.org Structured Data Definitions for Dr. Sunil Apsingi Orthopedics
 * Compliant with Google Search Central Medical and Local Business specifications.
 */

export const BASE_URL = "https://arthroscopydoc.com";

// Base Physician & Medical Clinic Schema (Home & Global Fallback)
export const doctorPhysicianSchema = {
  "@context": "https://schema.org",
  "@type": ["Physician", "MedicalBusiness"],
  "@id": `${BASE_URL}/#physician`,
  "name": "Dr. Sunil Apsingi",
  "alternateName": "Dr. Sunil Apsingi Orthopedic Surgeon",
  "jobTitle": "Chief Orthopedic, Arthroscopy & Joint Replacement Surgeon",
  "description": "Dr. Sunil Apsingi (MS Ortho PGI Chandigarh) is a renowned Chief Arthroscopy & Robotic Joint Replacement Surgeon with 23+ years of clinical excellence in HITEC City, Hyderabad.",
  "image": `${BASE_URL}/images/2022-05-13.png`,
  "url": BASE_URL,
  "telephone": "+91-8341555765",
  "priceRange": "$$",
  "currenciesAccepted": "INR",
  "paymentAccepted": "Cash, Credit Card, Debit Card, UPI, Health Insurance",
  "medicalSpecialty": [
    "OrthopedicSurgery",
    "JointReplacement",
    "Arthroscopy",
    "SportsMedicine"
  ],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Medicover Out Patient Center, HUDA Techno Enclave",
    "addressLocality": "HITEC City, Madhapur, Hyderabad",
    "addressRegion": "Telangana",
    "postalCode": "500081",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 17.4474,
    "longitude": 78.3762
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "166",
    "bestRating": "5",
    "worstRating": "1"
  },
  "alumniOf": {
    "@type": "EducationalOrganization",
    "name": "Post Graduate Institute of Medical Education and Research (PGIMER), Chandigarh"
  },
  "hospitalAffiliation": {
    "@type": "Hospital",
    "name": "Medicover Hospitals",
    "url": "https://www.medicoverhospitals.in"
  },
  "sameAs": [
    "https://www.youtube.com/@DrSunilApsingi",
    "https://www.linkedin.com/in/dr-sunil-apsingi"
  ]
};

// Medical Procedures & FAQ Schema for Services Page
export const servicesFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Arthroscopy and what are its clinical advantages?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Arthroscopy is a minimally invasive surgical procedure that allows an orthopedic surgeon to visualize, diagnose, and treat joint conditions through keyhole incisions. Advantages include minimal soft-tissue disruption, dramatically lower infection rates, faster rehabilitation, and same-day or 24-hour hospital discharge."
      }
    },
    {
      "@type": "Question",
      "name": "How quickly can I walk after robotic knee replacement surgery?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "With advanced robotic precision and muscle-sparing approaches practiced by Dr. Sunil Apsingi, most patients begin assisted standing and walking within 12 to 24 hours post-surgery. Full independent mobility typically resumes within 2 to 4 weeks under guided physiotherapy."
      }
    },
    {
      "@type": "Question",
      "name": "What conditions are treated with shoulder arthroscopy?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Shoulder arthroscopy treats recurrent shoulder dislocations (Bankart lesions), rotator cuff tears, subacromial impingement syndrome, frozen shoulder (capsular release), and labral SLAP tears."
      }
    },
    {
      "@type": "Question",
      "name": "Are health insurance policies accepted for surgical procedures?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Medicover Hospitals supports cashless hospitalization across all leading health insurance providers (TPAs) and corporate health coverages in India."
      }
    },
    {
      "@type": "Question",
      "name": "How can I book an in-person consultation with Dr. Sunil Apsingi?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You can book directly via this portal at arthroscopydoc.com/bookappointment, call +91-8341555765, or visit Medicover Out Patient Center in HITEC City, Hyderabad."
      }
    }
  ]
};

export const medicalProceduresSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  "name": "Orthopedic & Arthroscopic Surgical Specialties | Dr. Sunil Apsingi",
  "url": `${BASE_URL}/services`,
  "mainEntity": [
    {
      "@type": "MedicalProcedure",
      "name": "Robotic Knee Replacement Surgery",
      "procedureType": "SurgicalProcedure",
      "bodyLocation": "Knee",
      "description": "Sub-millimeter accurate joint resurfacing using 3D CT robotics for optimal implant alignment, natural joint kinematics, and faster recovery."
    },
    {
      "@type": "MedicalProcedure",
      "name": "Knee Arthroscopy & ACL Reconstruction",
      "procedureType": "SurgicalProcedure",
      "bodyLocation": "Knee",
      "description": "Minimally invasive anatomical anterior cruciate ligament (ACL) reconstruction, meniscus repair, and cartilage restoration."
    },
    {
      "@type": "MedicalProcedure",
      "name": "Shoulder Arthroscopy & Rotator Cuff Repair",
      "procedureType": "SurgicalProcedure",
      "bodyLocation": "Shoulder",
      "description": "Keyhole reconstruction for recurrent shoulder instability (Bankart repair), rotator cuff re-anchoring, and subacromial decompression."
    },
    {
      "@type": "MedicalProcedure",
      "name": "Sports Medicine & Ligament Rehabilitation",
      "procedureType": "NoninvasiveProcedure",
      "bodyLocation": "Joints",
      "description": "Comprehensive sports injury management, multi-ligament joint preservation, and athletic return-to-play protocols."
    }
  ]
};

// Profile Page Schema (About Us / Profile)
export const doctorProfileSchema = {
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "mainEntity": {
    "@type": "Person",
    "name": "Dr. Sunil Apsingi",
    "jobTitle": "Chief Orthopedic & Arthroscopy Surgeon",
    "alumniOf": {
      "@type": "EducationalOrganization",
      "name": "Post Graduate Institute of Medical Education and Research (PGIMER), Chandigarh",
      "sameAs": "https://pgimer.edu.in"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "degree",
        "name": "MS Orthopaedics"
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "fellowship",
        "name": "Fellowship in Arthroscopy & Sports Medicine"
      }
    ],
    "description": "Senior Orthopedic Consultant with over 23 years of specialized experience in arthroscopic surgery, joint replacement, and advanced sports injury care at Medicover Hospitals, Hyderabad.",
    "worksFor": {
      "@type": "Hospital",
      "name": "Medicover Hospitals",
      "address": "HITEC City, Hyderabad"
    }
  }
};

// Helper: Generate dynamic BlogPosting schema for articles
export const createBlogPostingSchema = (article) => {
  if (!article) return null;
  const publishDate = article.datetime ? new Date(article.datetime).toISOString() : new Date().toISOString();
  const title = article.title || "Orthopedic Care Article";
  const author = article.author || "Dr. Sunil Apsingi";
  const imageUrl = article.poster ? (article.poster.startsWith("http") ? article.poster : `${BASE_URL}${article.poster}`) : `${BASE_URL}/images/2022-05-13.png`;
  const url = `${BASE_URL}/blogs/${article.id}`;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": article.specialist ? `${article.specialist} - Written by ${author}` : `Expert clinical insight by ${author}`,
    "image": imageUrl,
    "datePublished": publishDate,
    "dateModified": publishDate,
    "author": {
      "@type": "Person",
      "name": author,
      "url": `${BASE_URL}/profile`
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dr. Sunil Apsingi Orthopedics",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/images/logo_new.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  };
};

// Appointment Booking Schema
export const appointmentBookingSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Book Orthopedic Consultation | Dr. Sunil Apsingi",
  "url": `${BASE_URL}/bookappointment`,
  "potentialAction": {
    "@type": "ReserveAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${BASE_URL}/bookappointment`,
      "inLanguage": "en",
      "actionPlatform": [
        "http://schema.org/DesktopWebPlatform",
        "http://schema.org/MobileWebPlatform"
      ]
    },
    "result": {
      "@type": "Reservation",
      "name": "Orthopedic Consultation with Dr. Sunil Apsingi"
    }
  }
};

// Contact Page Schema
export const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Dr. Sunil Apsingi Orthopedic Clinic",
  "url": `${BASE_URL}/contact`,
  "mainEntity": {
    "@type": "MedicalClinic",
    "name": "Dr. Sunil Apsingi Clinic at Medicover OPC",
    "telephone": "+91-8341555765",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Medicover Out Patient Center, HUDA Techno Enclave",
      "addressLocality": "HITEC City, Madhapur, Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500081",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 17.4474,
      "longitude": 78.3762
    }
  }
};
